import { Field } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row, Stack } from 'react-bootstrap';
import { FormLabel, FormStepHeader } from '../../../../../../components/forms';
import { Recipe } from '../../../../../../types/lessonPlan.type';
import { generateFileUrlSegment } from '../../../../../../utils/utils';
import { StepProps } from '../AddEditRecipe';

export const RecipeImageElement = ({
  onChange,
  onRemove,
  index,
  file,
  metadata,
  recipe
}: {
  onChange: any;
  onRemove: any;
  index: number;
  file: File | null | undefined;
  metadata: { id?: number; source?: string; destroy: boolean } | undefined;
  recipe?: Recipe;
}) => {
  const allowedTypes = ['image/jpeg', 'image/png'];
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] || null;

    if (!file) {
      onChange({ file: null });
      return;
    }

    //validate file type
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG and PNG files are allowed.');
      event.target.value = ''; //reset the input
      onChange({ file: null }); //clear Fromik field
      return;
    }

    setError(null);
    onChange({ file: file });
  };
  return (
    <div
      className="d-flex align-items-center justify-content-between px-lg-2 px-2 py-2"
      style={{
        background: '#F2F2F2',
        border: '1px solid rgba(94, 100, 105, .5)',
        borderRadius: 8
      }}
    >
      <Stack direction="vertical">
        {!metadata?.id && (
          <Form.Group>
            <FormLabel>Image</FormLabel>
            <input
              name={`images[${index}]`}
              ref={fileRef}
              className="form-control"
              accept="image/*"
              type="file"
              onChange={handleFileChange}
            />
            {/* Display the name of the file if it exists */}
            {file && <div className="mt-2">Selected file: {file.name}</div>}
            {error && (
              <div
                className="text-danger mt-1"
                style={{ fontSize: '0.875rem' }}
              >
                {error}
              </div>
            )}
          </Form.Group>
        )}
        {recipe?.recipe_images[index] && (
          <Row>
            <Col>
              <img
                  src={recipe?.recipe_images[index].signed_url ??
                      `${generateFileUrlSegment(recipe?.recipe_images[index].id, recipe?.recipe_images[index].s3_base_url, 'thumb')}/${recipe?.recipe_images[index].image_file_name}`}                alt={recipe?.recipe_images[index].source}
                className="image-recipe"
              />
            </Col>
          </Row>
        )}
        <Form.Group controlId={`image_metadata[${index}].source`}>
          <FormLabel>Source</FormLabel>
          <Field
            name={`image_metadata[${index}].source`}
            type="text"
            as={Form.Control}
          />
        </Form.Group>
        <Form.Group controlId={`image_metadata[${index}].destroy`}>
          <Form.Label>
            <Field
              type="checkbox"
              name={`image_metadata[${index}].destroy`}
            />
            Destroy
          </Form.Label>
        </Form.Group>
        {!metadata?.id && (
          <Button
            variant="primary"
            size="sm"
            className="btn-box-shadow text-uppercase border-0"
            style={{ width: 200 }}
            onClick={() => {
              onRemove();
            }}
          >
            Remove
          </Button>
        )}
      </Stack>
    </div>
  );
};

export const RecipeImagesForm = ({
  touched,
  errors,
  values,
  setFieldValue,
  readOnly,
  recipe
}: StepProps) => {
  const [chunks, setChunks] = useState<any[][]>([]);
  const handleChangeRegister = (data: any, index: any) => {
    const images = [];
    if (values!.images) {
      images.push(...values!.images);
    }
    setFieldValue!(
      'images',
      images!.map((image: any, i: any) => {
        if (i === index) {
          return data.file;
        }
        return image;
      })
    );
  };
  const handleRemove = (index: any) => {
    let images: any[] = [];
    let imageMetadata: any[] = [];
    if (values!.image_metadata) {
      imageMetadata = [...values!.image_metadata];
      imageMetadata.splice(index, 1);
    }
    if (values!.images) {
      images = [...values!.images];
      images.splice(index, 1);
    }

    setFieldValue!('images', images);
    setFieldValue!('image_metadata', imageMetadata);
  };
  const handleClick = () => {
    setFieldValue!('images', [...values!.images!, null]);
    const fileMetadata = {
      source: '',
      destroy: false
    };
    setFieldValue!('image_metadata', [...values!.image_metadata!, fileMetadata]);
  };
  const chunkArray = (array: any[], chunkSize: number) => {
    const result = [];
    if (array.length) {
      for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
      }
    }
    return result;
  };
  useEffect(() => {
    setChunks(chunkArray(values.image_metadata, 3));
  }, [values.image_metadata]);
  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Recipe Images" showRequiredLegend={false} />
      </Col>
      <Col xs={12}>
        <Button
          variant="primary"
          size="sm"
          className="btn-box-shadow text-uppercase border-0"
          style={{ width: 200 }}
          onClick={() => handleClick()}
        >
          Add New Recipe Image
        </Button>
        {chunks.map((chunk, index) => (
          <Row key={`file-row-${index}`}>
            {chunk.map((image, idx) => (
              <Col
                key={`file-col-${idx}`}
                xs={12}
                lg={4}
                className="mb-3"
              >
                <RecipeImageElement
                  key={`file-${idx}`}
                  onChange={(obj: any) => handleChangeRegister(obj, idx)}
                  index={idx}
                  onRemove={() => handleRemove(idx)}
                  file={values.images[idx]}
                  metadata={values.image_metadata![idx]}
                  recipe={recipe}
                />
              </Col>
            ))}
          </Row>
        ))}
      </Col>
    </Row>
  );
};
